import { useCallback } from 'react'
import { useMutation } from '@apollo/client/react'
import { AdminAPI, GraphQL } from '@guestgoodie/graphql'
import { useLingui } from '@lingui/react/macro'

import * as Form from '@/features/form'
import { GoodieFile, useUploadFile } from '@/features/files'
import { formToGraphql, FormTranslations } from '@/features/translations'

export type FormData = {
  name: string
  cover: GoodieFile
  sortBy: GraphQL.CategorySortTypes
  translations: FormTranslations
}

export const TRANSLATABLE_FIELDS: (keyof FormData)[] = ['name']

interface Options {
  layoutId?: number
  id?: number
  onDone?: () => void
}

export default function useCategoryForm(options: Options) {
  const { layoutId, id, onDone } = options

  const { t } = useLingui()
  const [uploadFile] = useUploadFile()

  const [createCategory] = useMutation(AdminAPI.category.CREATE, {
    refetchQueries: [AdminAPI.category.GET_BY_LAYOUT]
  })

  const [updateCategory] = useMutation(AdminAPI.category.UPDATE, {
    refetchQueries: [AdminAPI.category.GET_BY_LAYOUT]
  })

  const { errors, handlePromiseCatch } = Form.useErrorHelpers({
    mapper(field, message) {
      if (field === 'slug' && message === 'has already been taken') {
        return [
          'name',
          {
            type: message,
            message: t`The category name exists already.`
          }
        ]
      }
    }
  })

  const create = useCallback(
    ({ cover, ...rest }: FormData) => {
      if (!layoutId) return

      return uploadFile(cover)
        .then(({ id: coverId }) => {
          return createCategory({
            variables: {
              layoutId,
              input: {
                ...rest,
                coverId,
                translations: formToGraphql(rest.translations)
              }
            }
          })
        })
        .then(() => onDone?.())
        .catch(error => handlePromiseCatch(error))
    },
    [layoutId, createCategory, uploadFile, onDone, handlePromiseCatch]
  )

  const update = useCallback(
    async ({ cover, ...rest }: FormData) => {
      if (!id) return

      return uploadFile(cover)
        .then(({ id: coverId }) => {
          return updateCategory({
            variables: {
              input: {
                ...rest,
                id,
                coverId,
                translations: formToGraphql(rest.translations)
              }
            }
          })
        })
        .then(() => onDone?.())
        .catch(error => handlePromiseCatch(error))
    },
    [id, updateCategory, uploadFile, onDone, handlePromiseCatch]
  )

  return {
    create,
    update,
    errors
  }
}
